import { clsx } from 'clsx';
import { parseISO } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { twMerge } from 'tailwind-merge';

// src/utils/index.ts

// src/constants/index.ts
var RESERVATION_STATUS_TRANSITIONS = {
  pending: ["confirmed", "cancelled"],
  confirmed: ["assigned", "cancelled"],
  assigned: ["in_progress", "confirmed", "cancelled"],
  in_progress: ["completed", "cancelled"],
  completed: [],
  cancelled: []
};

// src/utils/geoMath.ts
var EARTH_RADIUS_MILES = 3958.8;
var KM_PER_MILE = 1.60934;
function haversineDistanceMiles(lat1, lng1, lat2, lng2) {
  const toRad = (deg) => deg * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(a));
}
function haversineDistanceKm(lat1, lng1, lat2, lng2) {
  return haversineDistanceMiles(lat1, lng1, lat2, lng2) * KM_PER_MILE;
}
function estimateDriveTimeMinutes(distanceMiles, avgSpeedMph = 25) {
  if (distanceMiles <= 0 || avgSpeedMph <= 0) return 0;
  return Math.round(distanceMiles / avgSpeedMph * 60);
}

// src/data/airports.ts
var AIRPORTS = [
  // ── United States: Large/Medium Hubs ────────────────────────────────────────
  { iata: "ATL", name: "Hartsfield\u2013Jackson Atlanta", lat: 33.6407, lng: -84.4277 },
  { iata: "LAX", name: "Los Angeles International", lat: 33.9416, lng: -118.4085 },
  { iata: "ORD", name: "Chicago O'Hare International", lat: 41.9742, lng: -87.9073 },
  { iata: "DFW", name: "Dallas/Fort Worth International", lat: 32.8998, lng: -97.0403 },
  { iata: "DEN", name: "Denver International", lat: 39.8561, lng: -104.6737 },
  { iata: "JFK", name: "New York John F. Kennedy", lat: 40.6413, lng: -73.7781 },
  { iata: "SFO", name: "San Francisco International", lat: 37.6213, lng: -122.379 },
  { iata: "SEA", name: "Seattle-Tacoma International", lat: 47.4502, lng: -122.3088 },
  { iata: "LAS", name: "Harry Reid International (Las Vegas)", lat: 36.084, lng: -115.1537 },
  { iata: "MCO", name: "Orlando International", lat: 28.4312, lng: -81.3081 },
  { iata: "EWR", name: "Newark Liberty International", lat: 40.6895, lng: -74.1745 },
  { iata: "PHX", name: "Phoenix Sky Harbor International", lat: 33.4373, lng: -112.0078 },
  { iata: "IAH", name: "Houston George Bush Intercontinental", lat: 29.9902, lng: -95.3368 },
  { iata: "CLT", name: "Charlotte Douglas International", lat: 35.2144, lng: -80.9473 },
  { iata: "MIA", name: "Miami International", lat: 25.7959, lng: -80.287 },
  { iata: "BOS", name: "Boston Logan International", lat: 42.3656, lng: -71.0096 },
  { iata: "MSP", name: "Minneapolis-Saint Paul International", lat: 44.8848, lng: -93.2223 },
  { iata: "DTW", name: "Detroit Metropolitan Wayne County", lat: 42.2162, lng: -83.3554 },
  { iata: "FLL", name: "Fort Lauderdale-Hollywood International", lat: 26.0726, lng: -80.1527 },
  { iata: "PHL", name: "Philadelphia International", lat: 39.8721, lng: -75.2437 },
  { iata: "LGA", name: "New York LaGuardia", lat: 40.7769, lng: -73.874 },
  { iata: "BWI", name: "Baltimore/Washington International", lat: 39.1754, lng: -76.6682 },
  { iata: "SLC", name: "Salt Lake City International", lat: 40.7899, lng: -111.9791 },
  { iata: "IAD", name: "Washington Dulles International", lat: 38.9531, lng: -77.4565 },
  { iata: "DCA", name: "Ronald Reagan Washington National", lat: 38.8512, lng: -77.0402 },
  { iata: "MDW", name: "Chicago Midway International", lat: 41.7868, lng: -87.7522 },
  { iata: "TPA", name: "Tampa International", lat: 27.9755, lng: -82.5332 },
  { iata: "PDX", name: "Portland International", lat: 45.5898, lng: -122.5951 },
  { iata: "BNA", name: "Nashville International", lat: 36.1245, lng: -86.6782 },
  { iata: "AUS", name: "Austin-Bergstrom International", lat: 30.1975, lng: -97.6664 },
  { iata: "MSY", name: "Louis Armstrong New Orleans International", lat: 29.9934, lng: -90.258 },
  { iata: "RDU", name: "Raleigh-Durham International", lat: 35.8776, lng: -78.7875 },
  { iata: "SAN", name: "San Diego International", lat: 32.7338, lng: -117.1933 },
  { iata: "HNL", name: "Daniel K. Inouye International (Honolulu)", lat: 21.3187, lng: -157.9224 },
  { iata: "STL", name: "St. Louis Lambert International", lat: 38.7487, lng: -90.37 },
  { iata: "DAL", name: "Dallas Love Field", lat: 32.8471, lng: -96.8517 },
  { iata: "HOU", name: "William P. Hobby Airport (Houston)", lat: 29.6454, lng: -95.2789 },
  { iata: "SAT", name: "San Antonio International", lat: 29.5337, lng: -98.4698 },
  { iata: "OAK", name: "Oakland Metropolitan International", lat: 37.7213, lng: -122.2208 },
  { iata: "SJC", name: "San Jos\xE9 Mineta International", lat: 37.3626, lng: -121.929 },
  { iata: "SMF", name: "Sacramento International", lat: 38.6954, lng: -121.5908 },
  { iata: "MCI", name: "Kansas City International", lat: 39.2976, lng: -94.7139 },
  { iata: "ABQ", name: "Albuquerque International Sunport", lat: 35.0402, lng: -106.609 },
  { iata: "CLE", name: "Cleveland Hopkins International", lat: 41.4117, lng: -81.8498 },
  { iata: "CMH", name: "John Glenn Columbus International", lat: 39.998, lng: -82.8919 },
  { iata: "IND", name: "Indianapolis International", lat: 39.7173, lng: -86.2944 },
  { iata: "PIT", name: "Pittsburgh International", lat: 40.4915, lng: -80.2329 },
  { iata: "CVG", name: "Cincinnati/Northern Kentucky International", lat: 39.0485, lng: -84.6678 },
  { iata: "MKE", name: "Milwaukee Mitchell International", lat: 42.9472, lng: -87.8966 },
  { iata: "MEM", name: "Memphis International", lat: 35.0421, lng: -89.9767 },
  { iata: "OMA", name: "Eppley Airfield (Omaha)", lat: 41.3032, lng: -95.894 },
  { iata: "BOI", name: "Boise Airport", lat: 43.5644, lng: -116.2228 },
  { iata: "SDF", name: "Louisville Muhammad Ali International", lat: 38.1744, lng: -85.736 },
  { iata: "LEX", name: "Blue Grass Airport (Lexington)", lat: 38.0365, lng: -84.606 },
  { iata: "GRR", name: "Gerald R. Ford International (Grand Rapids)", lat: 42.8808, lng: -85.5228 },
  { iata: "TYS", name: "McGhee Tyson Airport (Knoxville)", lat: 35.811, lng: -83.994 },
  { iata: "OGG", name: "Kahului Airport (Maui)", lat: 20.8986, lng: -156.4305 },
  { iata: "KOA", name: "Ellison Onizuka Kona International", lat: 19.7388, lng: -156.0456 },
  { iata: "LIH", name: "Lihue Airport (Kauai)", lat: 21.976, lng: -159.3388 },
  { iata: "ITO", name: "Hilo International", lat: 19.7213, lng: -155.0484 },
  { iata: "ANC", name: "Ted Stevens Anchorage International", lat: 61.1741, lng: -149.9963 },
  { iata: "FAI", name: "Fairbanks International", lat: 64.8151, lng: -147.8562 },
  { iata: "JNU", name: "Juneau International", lat: 58.355, lng: -134.5763 },
  { iata: "KTN", name: "Ketchikan International", lat: 55.3556, lng: -131.7137 },
  // ── United States: Small Hubs / Large Non-Hub ───────────────────────────────
  { iata: "PVD", name: "T.F. Green Providence", lat: 41.7237, lng: -71.4282 },
  { iata: "BUF", name: "Buffalo Niagara International", lat: 42.9405, lng: -78.7322 },
  { iata: "ALB", name: "Albany International", lat: 42.7483, lng: -73.8017 },
  { iata: "BDL", name: "Bradley International (Hartford)", lat: 41.9389, lng: -72.6832 },
  { iata: "MHT", name: "Manchester-Boston Regional", lat: 42.9326, lng: -71.4357 },
  { iata: "PWM", name: "Portland International Jetport (Maine)", lat: 43.6462, lng: -70.3093 },
  { iata: "BGR", name: "Bangor International", lat: 44.8074, lng: -68.8281 },
  { iata: "SYR", name: "Syracuse Hancock International", lat: 43.1112, lng: -76.1066 },
  { iata: "ROC", name: "Greater Rochester International", lat: 43.1189, lng: -77.6724 },
  { iata: "HPN", name: "Westchester County Airport", lat: 41.067, lng: -73.7076 },
  { iata: "ISP", name: "Long Island MacArthur Airport", lat: 40.7952, lng: -73.1002 },
  { iata: "ORF", name: "Norfolk International", lat: 36.8976, lng: -76.0133 },
  { iata: "RIC", name: "Richmond International", lat: 37.5052, lng: -77.3197 },
  { iata: "GSO", name: "Piedmont Triad International (Greensboro)", lat: 36.0978, lng: -79.9373 },
  { iata: "GSP", name: "Greenville-Spartanburg International", lat: 34.8957, lng: -82.2189 },
  { iata: "CAE", name: "Columbia Metropolitan", lat: 33.9389, lng: -81.1195 },
  { iata: "CHS", name: "Charleston International", lat: 32.8987, lng: -80.0405 },
  { iata: "SAV", name: "Savannah/Hilton Head International", lat: 32.1277, lng: -81.2021 },
  { iata: "JAX", name: "Jacksonville International", lat: 30.4941, lng: -81.6879 },
  { iata: "RSW", name: "Southwest Florida International (Fort Myers)", lat: 26.5362, lng: -81.7552 },
  { iata: "PBI", name: "Palm Beach International", lat: 26.6832, lng: -80.0956 },
  { iata: "SRQ", name: "Sarasota Bradenton International", lat: 27.3954, lng: -82.5543 },
  { iata: "TUS", name: "Tucson International", lat: 32.1161, lng: -110.941 },
  { iata: "ELP", name: "El Paso International", lat: 31.8072, lng: -106.3779 },
  { iata: "LBB", name: "Lubbock Preston Smith International", lat: 33.6636, lng: -101.8228 },
  { iata: "MAF", name: "Midland International Air & Space Port", lat: 31.9425, lng: -102.2019 },
  { iata: "TUL", name: "Tulsa International", lat: 36.1984, lng: -95.8881 },
  { iata: "OKC", name: "Will Rogers World Airport (Oklahoma City)", lat: 35.3931, lng: -97.6007 },
  { iata: "XNA", name: "Northwest Arkansas National", lat: 36.2819, lng: -94.3068 },
  { iata: "LIT", name: "Bill and Hillary Clinton National (Little Rock)", lat: 34.7294, lng: -92.2243 },
  { iata: "SHV", name: "Shreveport Regional", lat: 32.4466, lng: -93.8256 },
  { iata: "BTR", name: "Baton Rouge Metropolitan", lat: 30.5332, lng: -91.1496 },
  { iata: "MSN", name: "Dane County Regional (Madison)", lat: 43.1399, lng: -89.3375 },
  { iata: "DSM", name: "Des Moines International", lat: 41.534, lng: -93.6631 },
  { iata: "MLI", name: "Quad City International", lat: 41.4485, lng: -90.5075 },
  { iata: "CID", name: "The Eastern Iowa Airport (Cedar Rapids)", lat: 41.8842, lng: -91.7108 },
  { iata: "FAR", name: "Hector International (Fargo)", lat: 46.9207, lng: -96.8158 },
  { iata: "FSD", name: "Sioux Falls Regional (Joe Foss Field)", lat: 43.582, lng: -96.7419 },
  { iata: "BIS", name: "Bismarck Municipal", lat: 46.7724, lng: -100.7463 },
  { iata: "RAP", name: "Rapid City Regional", lat: 44.0453, lng: -103.0574 },
  { iata: "ICT", name: "Dwight D. Eisenhower National (Wichita)", lat: 37.6499, lng: -97.4331 },
  { iata: "SNA", name: "John Wayne Airport (Orange County)", lat: 33.6757, lng: -117.8682 },
  { iata: "BUR", name: "Hollywood Burbank Airport", lat: 34.2006, lng: -118.3585 },
  { iata: "LGB", name: "Long Beach Airport", lat: 33.8117, lng: -118.1516 },
  { iata: "ONT", name: "Ontario International", lat: 34.056, lng: -117.6012 },
  { iata: "PSP", name: "Palm Springs International", lat: 33.8297, lng: -116.5067 },
  { iata: "FAT", name: "Fresno Yosemite International", lat: 36.7762, lng: -119.7182 },
  { iata: "SBA", name: "Santa Barbara Municipal", lat: 34.4263, lng: -119.8403 },
  { iata: "MRY", name: "Monterey Regional", lat: 36.587, lng: -121.8428 },
  { iata: "RNO", name: "Reno-Tahoe International", lat: 39.4991, lng: -119.7681 },
  { iata: "GEG", name: "Spokane International", lat: 47.6199, lng: -117.5338 },
  { iata: "MFR", name: "Rogue Valley International (Medford)", lat: 42.3742, lng: -122.873 },
  { iata: "EUG", name: "Eugene Airport", lat: 44.1246, lng: -123.2119 },
  { iata: "RDD", name: "Redding Municipal", lat: 40.509, lng: -122.2932 },
  { iata: "ACV", name: "California Redwood Coast\u2013Humboldt County", lat: 40.9782, lng: -124.1087 },
  { iata: "GJT", name: "Grand Junction Regional", lat: 39.1224, lng: -108.5268 },
  { iata: "COS", name: "Colorado Springs Airport", lat: 38.8058, lng: -104.7008 },
  { iata: "MSO", name: "Missoula Montana Airport", lat: 46.9163, lng: -114.0906 },
  { iata: "HLN", name: "Helena Regional", lat: 46.6068, lng: -111.9829 },
  { iata: "GTF", name: "Great Falls International", lat: 47.482, lng: -111.3711 },
  { iata: "BIL", name: "Billings Logan International", lat: 45.8077, lng: -108.5428 },
  { iata: "CPR", name: "Casper/Natrona County International", lat: 42.908, lng: -106.4636 },
  { iata: "PIH", name: "Pocatello Regional", lat: 42.9098, lng: -112.5958 },
  { iata: "GFK", name: "Grand Forks International", lat: 47.9493, lng: -97.1761 },
  { iata: "SJU", name: "Luis Mu\xF1oz Mar\xEDn International (San Juan, PR)", lat: 18.4394, lng: -66.0018 },
  { iata: "BQN", name: "Rafael Hern\xE1ndez Airport (Aguadilla, PR)", lat: 18.4949, lng: -67.1289 },
  { iata: "STT", name: "Cyril E. King Airport (St. Thomas, USVI)", lat: 18.3373, lng: -64.9733 },
  { iata: "GUM", name: "Antonio B. Won Pat International (Guam)", lat: 13.4834, lng: 144.7963 },
  // ── Canada ──────────────────────────────────────────────────────────────────
  { iata: "YVR", name: "Vancouver International", lat: 49.1967, lng: -123.1815 },
  { iata: "YYZ", name: "Toronto Pearson International", lat: 43.6777, lng: -79.6248 },
  { iata: "YUL", name: "Montr\xE9al-Trudeau International", lat: 45.4706, lng: -73.7408 },
  { iata: "YYC", name: "Calgary International", lat: 51.1314, lng: -114.013 },
  { iata: "YEG", name: "Edmonton International", lat: 53.3097, lng: -113.5797 },
  { iata: "YOW", name: "Ottawa Macdonald\u2013Cartier International", lat: 45.3225, lng: -75.6692 },
  { iata: "YHZ", name: "Halifax Stanfield International", lat: 44.8808, lng: -63.5086 },
  // ── Mexico & Central America ─────────────────────────────────────────────────
  { iata: "MEX", name: "Mexico City International", lat: 19.4363, lng: -99.0721 },
  { iata: "CUN", name: "Canc\xFAn International", lat: 21.0365, lng: -86.8771 },
  { iata: "GDL", name: "Guadalajara Miguel Hidalgo International", lat: 20.5218, lng: -103.311 },
  { iata: "MTY", name: "Monterrey International", lat: 25.7785, lng: -100.107 },
  { iata: "PVR", name: "Puerto Vallarta International", lat: 20.6801, lng: -105.254 },
  { iata: "SJD", name: "Los Cabos International", lat: 23.1518, lng: -109.721 },
  { iata: "MZT", name: "General Rafael Buelna (Mazatl\xE1n)", lat: 23.1614, lng: -106.2661 },
  { iata: "TIJ", name: "General Abelardo L. Rodr\xEDguez (Tijuana)", lat: 32.5411, lng: -116.97 },
  { iata: "BJX", name: "Baj\xEDo International (Le\xF3n)", lat: 20.9935, lng: -101.4809 },
  { iata: "GUA", name: "La Aurora International (Guatemala City)", lat: 14.5833, lng: -90.5275 },
  { iata: "SJO", name: "Juan Santamar\xEDa International (San Jos\xE9, CR)", lat: 9.9939, lng: -84.2088 },
  { iata: "PTY", name: "Tocumen International (Panama City)", lat: 9.0714, lng: -79.3835 },
  // ── Caribbean ────────────────────────────────────────────────────────────────
  { iata: "MBJ", name: "Sangster International (Montego Bay)", lat: 18.5037, lng: -77.9134 },
  { iata: "KIN", name: "Norman Manley International (Kingston)", lat: 17.9357, lng: -76.7875 },
  { iata: "SDQ", name: "Las Am\xE9ricas International (Santo Domingo)", lat: 18.4297, lng: -69.6689 },
  { iata: "PUJ", name: "Punta Cana International", lat: 18.5674, lng: -68.3638 },
  { iata: "NAS", name: "Lynden Pindling International (Nassau)", lat: 25.0389, lng: -77.4662 },
  { iata: "GCM", name: "Owen Roberts International (Grand Cayman)", lat: 19.2928, lng: -81.3577 },
  { iata: "BGI", name: "Grantley Adams International (Bridgetown)", lat: 13.0746, lng: -59.4925 },
  { iata: "SXM", name: "Princess Juliana International (St. Maarten)", lat: 18.041, lng: -63.1089 },
  // ── South America ────────────────────────────────────────────────────────────
  { iata: "GRU", name: "S\xE3o Paulo\u2013Guarulhos International", lat: -23.4356, lng: -46.4731 },
  { iata: "GIG", name: "Rio de Janeiro\u2013Gale\xE3o International", lat: -22.8099, lng: -43.2505 },
  { iata: "BSB", name: "Presidente Juscelino Kubitschek (Bras\xEDlia)", lat: -15.8711, lng: -47.9186 },
  { iata: "EZE", name: "Ministro Pistarini International (Buenos Aires)", lat: -34.8222, lng: -58.5358 },
  { iata: "SCL", name: "Arturo Merino Ben\xEDtez International (Santiago)", lat: -33.393, lng: -70.7936 },
  { iata: "BOG", name: "El Dorado International (Bogot\xE1)", lat: 4.7016, lng: -74.1469 },
  { iata: "LIM", name: "Jorge Ch\xE1vez International (Lima)", lat: -12.0219, lng: -77.1143 },
  { iata: "MVD", name: "Carrasco International (Montevideo)", lat: -34.8384, lng: -56.0308 },
  { iata: "UIO", name: "Mariscal Sucre International (Quito)", lat: -0.1292, lng: -78.3575 },
  // ── Europe ───────────────────────────────────────────────────────────────────
  { iata: "LHR", name: "London Heathrow", lat: 51.4775, lng: -0.4614 },
  { iata: "LGW", name: "London Gatwick", lat: 51.1537, lng: -0.1821 },
  { iata: "STN", name: "London Stansted", lat: 51.885, lng: 0.235 },
  { iata: "MAN", name: "Manchester Airport", lat: 53.3537, lng: -2.275 },
  { iata: "EDI", name: "Edinburgh Airport", lat: 55.95, lng: -3.3725 },
  { iata: "CDG", name: "Paris Charles de Gaulle", lat: 49.0097, lng: 2.5479 },
  { iata: "ORY", name: "Paris Orly", lat: 48.7233, lng: 2.3794 },
  { iata: "AMS", name: "Amsterdam Schiphol", lat: 52.3086, lng: 4.7639 },
  { iata: "FRA", name: "Frankfurt am Main", lat: 50.0333, lng: 8.5706 },
  { iata: "MUC", name: "Munich International", lat: 48.3537, lng: 11.775 },
  { iata: "BER", name: "Berlin Brandenburg International", lat: 52.3667, lng: 13.5033 },
  { iata: "HAM", name: "Hamburg Airport", lat: 53.6304, lng: 9.9882 },
  { iata: "DUS", name: "D\xFCsseldorf International", lat: 51.2895, lng: 6.7668 },
  { iata: "ZRH", name: "Z\xFCrich Airport", lat: 47.4647, lng: 8.5492 },
  { iata: "GVA", name: "Geneva Airport", lat: 46.237, lng: 6.1089 },
  { iata: "VIE", name: "Vienna International", lat: 48.1102, lng: 16.5697 },
  { iata: "BRU", name: "Brussels Airport", lat: 50.9014, lng: 4.4844 },
  { iata: "CPH", name: "Copenhagen Airport", lat: 55.6181, lng: 12.656 },
  { iata: "ARN", name: "Stockholm Arlanda", lat: 59.6519, lng: 17.9186 },
  { iata: "OSL", name: "Oslo Gardermoen", lat: 60.1939, lng: 11.1 },
  { iata: "HEL", name: "Helsinki-Vantaa", lat: 60.3172, lng: 24.9633 },
  { iata: "LIS", name: "Lisbon Humberto Delgado", lat: 38.7813, lng: -9.1359 },
  { iata: "MAD", name: "Adolfo Su\xE1rez Madrid\u2013Barajas", lat: 40.4719, lng: -3.5626 },
  { iata: "BCN", name: "Barcelona\u2013El Prat", lat: 41.2971, lng: 2.0785 },
  { iata: "FCO", name: "Rome Fiumicino", lat: 41.8003, lng: 12.2389 },
  { iata: "MXP", name: "Milan Malpensa", lat: 45.6306, lng: 8.7281 },
  { iata: "VCE", name: "Venice Marco Polo", lat: 45.5052, lng: 12.3519 },
  { iata: "ATH", name: "Athens Eleftherios Venizelos", lat: 37.9364, lng: 23.9445 },
  { iata: "PRG", name: "V\xE1clav Havel Prague", lat: 50.1008, lng: 14.26 },
  { iata: "BUD", name: "Budapest Ferenc Liszt International", lat: 47.4298, lng: 19.2611 },
  { iata: "WAW", name: "Warsaw Chopin", lat: 52.1657, lng: 20.9671 },
  // ── Middle East ──────────────────────────────────────────────────────────────
  { iata: "DXB", name: "Dubai International", lat: 25.2528, lng: 55.3644 },
  { iata: "DWC", name: "Al Maktoum International (Dubai)", lat: 24.8963, lng: 55.1612 },
  { iata: "DOH", name: "Hamad International (Doha)", lat: 25.2731, lng: 51.6081 },
  { iata: "AUH", name: "Abu Dhabi International", lat: 24.433, lng: 54.6511 },
  { iata: "IST", name: "Istanbul Airport", lat: 41.2608, lng: 28.7418 },
  { iata: "SAW", name: "Istanbul Sabiha G\xF6k\xE7en", lat: 40.8985, lng: 29.3092 },
  { iata: "TLV", name: "Ben Gurion International (Tel Aviv)", lat: 32.0055, lng: 34.8854 },
  { iata: "AMM", name: "Queen Alia International (Amman)", lat: 31.7226, lng: 35.9932 },
  { iata: "CAI", name: "Cairo International", lat: 30.1219, lng: 31.4056 },
  // ── Africa ───────────────────────────────────────────────────────────────────
  { iata: "JNB", name: "O.R. Tambo International (Johannesburg)", lat: -26.1392, lng: 28.246 },
  { iata: "CPT", name: "Cape Town International", lat: -33.9648, lng: 18.6017 },
  { iata: "NBO", name: "Jomo Kenyatta International (Nairobi)", lat: -1.3192, lng: 36.9275 },
  { iata: "ADD", name: "Addis Ababa Bole International", lat: 8.9779, lng: 38.7993 },
  { iata: "LOS", name: "Murtala Muhammed International (Lagos)", lat: 6.5774, lng: 3.3214 },
  { iata: "CMN", name: "Mohammed V International (Casablanca)", lat: 33.3675, lng: -7.5897 },
  // ── Asia–Pacific ─────────────────────────────────────────────────────────────
  { iata: "BOM", name: "Chhatrapati Shivaji Maharaj International (Mumbai)", lat: 19.0896, lng: 72.8656 },
  { iata: "DEL", name: "Indira Gandhi International (New Delhi)", lat: 28.5562, lng: 77.1 },
  { iata: "BLR", name: "Kempegowda International (Bengaluru)", lat: 13.1986, lng: 77.7066 },
  { iata: "HYD", name: "Rajiv Gandhi International (Hyderabad)", lat: 17.2313, lng: 78.4298 },
  { iata: "MAA", name: "Chennai International", lat: 12.99, lng: 80.1693 },
  { iata: "CCU", name: "Netaji Subhas Chandra Bose International (Kolkata)", lat: 22.6545, lng: 88.4467 },
  { iata: "SIN", name: "Singapore Changi", lat: 1.3644, lng: 103.9915 },
  { iata: "KUL", name: "Kuala Lumpur International", lat: 2.7456, lng: 101.7099 },
  { iata: "BKK", name: "Suvarnabhumi (Bangkok)", lat: 13.6811, lng: 100.7473 },
  { iata: "DMK", name: "Don Mueang International (Bangkok)", lat: 13.9126, lng: 100.607 },
  { iata: "CGK", name: "Soekarno\u2013Hatta International (Jakarta)", lat: -6.1256, lng: 106.6559 },
  { iata: "DPS", name: "Ngurah Rai International (Bali)", lat: -8.7482, lng: 115.1675 },
  { iata: "MNL", name: "Ninoy Aquino International (Manila)", lat: 14.5086, lng: 121.0197 },
  { iata: "HAN", name: "Noi Bai International (Hanoi)", lat: 21.2212, lng: 105.8072 },
  { iata: "SGN", name: "Tan Son Nhat International (Ho Chi Minh City)", lat: 10.8188, lng: 106.6519 },
  { iata: "HKG", name: "Hong Kong International", lat: 22.308, lng: 113.9185 },
  { iata: "TPE", name: "Taiwan Taoyuan International", lat: 25.0777, lng: 121.2327 },
  { iata: "NRT", name: "Tokyo Narita International", lat: 35.7648, lng: 140.3864 },
  { iata: "HND", name: "Tokyo Haneda International", lat: 35.5494, lng: 139.7798 },
  { iata: "KIX", name: "Kansai International (Osaka)", lat: 34.4347, lng: 135.244 },
  { iata: "NGO", name: "Chubu Centrair International (Nagoya)", lat: 34.8583, lng: 136.8049 },
  { iata: "CTS", name: "New Chitose Airport (Sapporo)", lat: 42.7752, lng: 141.6922 },
  { iata: "FUK", name: "Fukuoka Airport", lat: 33.5853, lng: 130.4511 },
  { iata: "OKA", name: "Naha Airport (Okinawa)", lat: 26.1958, lng: 127.6461 },
  { iata: "ICN", name: "Incheon International (Seoul)", lat: 37.4602, lng: 126.4407 },
  { iata: "GMP", name: "Gimpo International (Seoul)", lat: 37.5583, lng: 126.7914 },
  { iata: "PEK", name: "Beijing Capital International", lat: 40.0799, lng: 116.6031 },
  { iata: "PKX", name: "Beijing Daxing International", lat: 39.5098, lng: 116.4112 },
  { iata: "PVG", name: "Shanghai Pudong International", lat: 31.1443, lng: 121.8083 },
  { iata: "SHA", name: "Shanghai Hongqiao International", lat: 31.1981, lng: 121.3365 },
  { iata: "CAN", name: "Guangzhou Baiyun International", lat: 23.3924, lng: 113.299 },
  { iata: "CTU", name: "Chengdu Tianfu International", lat: 30.3118, lng: 104.4447 },
  // ── Oceania ──────────────────────────────────────────────────────────────────
  { iata: "SYD", name: "Sydney Kingsford Smith", lat: -33.9461, lng: 151.1772 },
  { iata: "MEL", name: "Melbourne Airport", lat: -37.669, lng: 144.841 },
  { iata: "BNE", name: "Brisbane Airport", lat: -27.3842, lng: 153.1175 },
  { iata: "PER", name: "Perth Airport", lat: -31.9403, lng: 115.9669 },
  { iata: "ADL", name: "Adelaide Airport", lat: -34.945, lng: 138.53 },
  { iata: "OOL", name: "Gold Coast Airport", lat: -28.1644, lng: 153.505 },
  { iata: "CNS", name: "Cairns Airport", lat: -16.8858, lng: 145.755 },
  { iata: "AKL", name: "Auckland Airport", lat: -37.0082, lng: 174.7917 },
  { iata: "WLG", name: "Wellington International", lat: -41.3272, lng: 174.8052 },
  { iata: "CHC", name: "Christchurch International", lat: -43.4894, lng: 172.5319 },
  { iata: "ZQN", name: "Queenstown Airport", lat: -45.0211, lng: 168.7389 }
];

// src/utils/airports.ts
var AIRPORT_RADIUS_MILES = 3 / 1.60934;
var LAT_PAD = 0.03;
var LNG_PAD_BASE = 0.04;
var MIN_COS_LAT = 0.15;
function lngPadForLat(lat) {
  const cosLat = Math.max(Math.abs(Math.cos(lat * Math.PI / 180)), MIN_COS_LAT);
  return LNG_PAD_BASE / cosLat;
}
function isNearAirport(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  let best = null;
  const lngPad = lngPadForLat(lat);
  for (const airport of AIRPORTS) {
    if (Math.abs(airport.lat - lat) > LAT_PAD || Math.abs(airport.lng - lng) > lngPad) {
      continue;
    }
    const distanceMiles = haversineDistanceMiles(lat, lng, airport.lat, airport.lng);
    if (distanceMiles <= AIRPORT_RADIUS_MILES && (best === null || distanceMiles < best.distanceMiles)) {
      best = { airport, distanceMiles };
    }
  }
  return best;
}

// src/utils/stops.ts
function hasNoFlightWarning(reservation) {
  if (!reservation.noFlightFlag) return false;
  const isAirportRide = reservation.hasAirportEndpoint === true || reservation.subType === "arrival" || reservation.subType === "departure";
  return isAirportRide;
}
function deriveLocationsFromStops(stops) {
  const pickups = stops.filter((s) => s.role === "pickup").sort((a, b) => a.position - b.position);
  const dropoffs = stops.filter((s) => s.role === "dropoff").sort((a, b) => a.position - b.position);
  return {
    pickupLocation: pickups[0]?.address ?? null,
    dropoffLocation: dropoffs[dropoffs.length - 1]?.address ?? null
  };
}
function coordWaypointsForRole(stops, role) {
  return stops.filter((s) => s.role === role).sort((a, b) => a.position - b.position).flatMap(
    (s) => s.lat !== null && s.lng !== null ? [{ lat: s.lat, lng: s.lng }] : []
  );
}
function orderWaypointsForPricing(stops) {
  return [
    ...coordWaypointsForRole(stops, "pickup"),
    ...coordWaypointsForRole(stops, "dropoff")
  ];
}

// src/utils/index.ts
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function formatDateInTimezone(date, timezone, formatStr = "MMM dd, yyyy h:mm a zzz") {
  const dateObj = typeof date === "string" ? parseISO(date) : date;
  return formatInTimeZone(dateObj, timezone, formatStr);
}
function localToUtc(localDatetime, timezone) {
  const date = parseISO(localDatetime);
  return fromZonedTime(date, timezone);
}
function utcToLocal(utcDate, timezone) {
  const dateObj = typeof utcDate === "string" ? parseISO(utcDate) : utcDate;
  return toZonedTime(dateObj, timezone);
}
function formatShortDate(date, timezone) {
  return formatDateInTimezone(date, timezone, "MMM dd, yyyy");
}
function formatTime(date, timezone) {
  return formatDateInTimezone(date, timezone, "h:mm a");
}
function toISOString(date) {
  return date.toISOString();
}
function plural(n, unit) {
  return `${String(n)} ${unit}${n === 1 ? "" : "s"}`;
}
function evaluateBookingWindow(params) {
  const { now, timezone, policy } = params;
  const pickup = typeof params.pickup === "string" ? parseISO(params.pickup) : params.pickup;
  if (policy.minHoursNotice > 0) {
    const earliest = new Date(now.getTime() + policy.minHoursNotice * 60 * 60 * 1e3);
    if (pickup.getTime() < earliest.getTime()) {
      return {
        ok: false,
        violation: "too_soon",
        message: `Pickups need at least ${plural(policy.minHoursNotice, "hour")}' notice.`
      };
    }
  }
  const todayLocalIso = formatInTimeZone(now, timezone, "yyyy-MM-dd");
  const horizonMarker = /* @__PURE__ */ new Date(`${todayLocalIso}T00:00:00.000Z`);
  horizonMarker.setUTCDate(horizonMarker.getUTCDate() + policy.maxAdvanceBookingDays);
  const horizonLocalIso = `${formatDatePart(horizonMarker)}T23:59:59.999`;
  const latestAllowed = fromZonedTime(horizonLocalIso, timezone);
  if (pickup.getTime() > latestAllowed.getTime()) {
    return {
      ok: false,
      violation: "too_far",
      message: `Pickups can be booked at most ${plural(
        policy.maxAdvanceBookingDays,
        "day"
      )} in advance.`
    };
  }
  return { ok: true };
}
function formatDatePart(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${String(y)}-${m}-${day}`;
}
var RESERVED_SLUGS = [
  // System routes
  "admin",
  "api",
  "app",
  "auth",
  "book",
  "booking",
  "bookings",
  "dashboard",
  "login",
  "logout",
  "register",
  "signup",
  "signin",
  "settings",
  "account",
  "profile",
  "user",
  "users",
  // Subdomains / infrastructure
  "www",
  "mail",
  "email",
  "ftp",
  "cdn",
  "assets",
  "static",
  "media",
  "images",
  "img",
  "files",
  "docs",
  "help",
  "support",
  "status",
  "blog",
  // Platform terms
  "ridereserv",
  "ride-reserv",
  "driver",
  "drivers",
  "company",
  "companies",
  "fleet",
  "platform",
  "public",
  "private",
  // Common words that might cause confusion
  "test",
  "demo",
  "example",
  "sample",
  "null",
  "undefined",
  "true",
  "false",
  "new",
  "edit",
  "delete",
  "create",
  "update"
];
function slugify(text) {
  return text.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
}
function isReservedSlug(slug) {
  return RESERVED_SLUGS.includes(slug.toLowerCase());
}
function validateCompanySlug(slug) {
  if (slug.length < 2) {
    return "Slug must be at least 2 characters";
  }
  if (slug.length > 100) {
    return "Slug must be 100 characters or less";
  }
  if (!/^[a-z0-9-]+$/.test(slug)) {
    return "Slug can only contain lowercase letters, numbers, and hyphens";
  }
  if (slug.startsWith("-") || slug.endsWith("-")) {
    return "Slug cannot start or end with a hyphen";
  }
  if (slug.includes("--")) {
    return "Slug cannot contain consecutive hyphens";
  }
  if (isReservedSlug(slug)) {
    return "This URL is reserved and cannot be used";
  }
  return null;
}
function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
function formatPhone(phone) {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}
function formatPhoneInput(value) {
  const hasPlus = value.startsWith("+");
  const digits = value.replace(/\D/g, "");
  if (hasPlus && digits.length <= 10 && digits.length > 0) {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (hasPlus) {
    const cleaned = "+" + digits;
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 5) return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
    if (cleaned.length <= 8) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5)}`;
    if (cleaned.length <= 12) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8, 12)} ${cleaned.slice(12, 17)}`;
  }
  if (digits.length === 0) return "";
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return "+" + digits;
}
function normalizePhoneToE164(value) {
  if (!value) return "";
  const hasPlus = value.startsWith("+");
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  if (hasPlus) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }
  return `+${digits}`;
}
function isE164Format(value) {
  const e164Regex = /^\+[1-9]\d{0,14}$/;
  return e164Regex.test(value);
}
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
function isValidStatusTransition(currentStatus, newStatus) {
  const allowedTransitions = RESERVATION_STATUS_TRANSITIONS[currentStatus];
  return allowedTransitions.includes(newStatus);
}
function getAllowedTransitions(currentStatus) {
  return RESERVATION_STATUS_TRANSITIONS[currentStatus];
}
function formatReservationStatus(status) {
  const statusLabels = {
    pending: "Pending",
    confirmed: "Confirmed",
    assigned: "Assigned",
    in_progress: "In Progress",
    completed: "Completed",
    cancelled: "Cancelled"
  };
  return statusLabels[status];
}
function formatCurrency(amount, currency = "USD", locale = "en-US") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency
  }).format(amount);
}
function centsToDollars(cents) {
  return cents / 100;
}
function dollarsToCents(dollars) {
  return Math.round(dollars * 100);
}
function hasRole(userRoles, ...requiredRoles) {
  return requiredRoles.some((role) => userRoles.includes(role));
}
function hasAllRoles(userRoles, ...requiredRoles) {
  return requiredRoles.every((role) => userRoles.includes(role));
}
function getPlatformRolePermissions(role) {
  const rolePermissions = {
    super_admin: [
      "view_all_data",
      "edit_users",
      "edit_companies",
      "verify_companies",
      "edit_reservations",
      "manage_subscriptions",
      "manage_platform_admins",
      "view_analytics",
      "access_logs"
    ],
    support: [
      "view_all_data",
      "edit_users",
      "edit_companies",
      "verify_companies",
      "edit_reservations"
    ],
    verifier: ["view_all_data", "verify_companies"],
    developer: ["view_all_data", "view_analytics", "access_logs"],
    read_only: ["view_all_data"]
  };
  return rolePermissions[role];
}
function hasPlatformPermission(admin, permission) {
  if (admin.permissions.includes(permission)) {
    return true;
  }
  const rolePermissions = getPlatformRolePermissions(admin.role);
  return rolePermissions.includes(permission);
}
function addressPart(source, key) {
  const value = source[key];
  return typeof value === "string" ? value.trim() : "";
}
function formatCompanyAddress(address) {
  if (typeof address === "string") {
    return address.trim();
  }
  if (address === null || typeof address !== "object" || Array.isArray(address)) {
    return "";
  }
  const source = address;
  const parts = [];
  const line1 = addressPart(source, "addressLine1");
  const line2 = addressPart(source, "addressLine2");
  if (line1) parts.push(line1);
  if (line2) parts.push(line2);
  const city = addressPart(source, "city");
  const stateOrRegion = addressPart(source, "stateOrRegion");
  const postalCode = addressPart(source, "postalCode");
  const stateZip = [stateOrRegion, postalCode].filter(Boolean).join(" ");
  const locality = [city, stateZip].filter(Boolean).join(", ");
  if (locality) parts.push(locality);
  const country = addressPart(source, "country");
  if (country) parts.push(country);
  return parts.join(", ");
}

export { RESERVED_SLUGS, capitalize, centsToDollars, cn, deriveLocationsFromStops, dollarsToCents, estimateDriveTimeMinutes, evaluateBookingWindow, formatCompanyAddress, formatCurrency, formatDateInTimezone, formatPhone, formatPhoneInput, formatReservationStatus, formatShortDate, formatTime, getAllowedTransitions, getPlatformRolePermissions, hasAllRoles, hasNoFlightWarning, hasPlatformPermission, hasRole, haversineDistanceKm, haversineDistanceMiles, isE164Format, isNearAirport, isReservedSlug, isValidStatusTransition, localToUtc, normalizePhoneToE164, orderWaypointsForPricing, slugify, toISOString, truncate, utcToLocal, validateCompanySlug };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map